import type { ReactNode } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { Dropzone } from '@teach/ui'
import { WebInstructionCard } from '@/features/instructions/components/editor/preview/web/WebInstructionCard'
import { Type1Banner } from '@/features/instructions/components/editor/preview/Type1Banner'
import { Type2Cover } from '@/features/instructions/components/editor/preview/Type2Cover'
import { UploadProgressOverlay } from '@/features/editor/components/shared/UploadProgressOverlay'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface BaseProps {
  title: string
  coverType: 'type1' | 'type2'
  gradient: string | null
  cardImage: string | null
  titleColor: string | null
  onUploadClick?: () => void
}

interface SquareProps extends BaseProps {
  /** Процент загрузки обложки (`null` — не грузим). */
  progress?: number | null
  authorName?: string | null
  authorAvatar?: string | null
  viewsCount?: number
}

type CoverMessages = (typeof messages)['ru']

const squarePlaceholder = (m: CoverMessages, onUploadClick?: () => void): ReactNode => (
  <Dropzone
    variant="onMedia"
    icon={ImageIcon}
    title={m.uploadCover}
    subtitle={m.uploadHint}
    onClick={onUploadClick}
    // Без обработчика (статья на проверке) зона погашена — иначе фальшивая кнопка.
    disabled={!onUploadClick}
    className="max-w-[78%] !p-4"
  />
)

/** Full-width "Шапка" banner preview (4:1). */
export const CoverBanner = ({
  title,
  coverType,
  gradient,
  cardImage,
  titleColor,
  onUploadClick,
}: BaseProps) => {
  const m = useMessages(messages)
  return coverType === 'type1' ? (
    <Type1Banner
      title={title}
      gradient={gradient}
      cardImage={cardImage}
      titleColor={titleColor}
      onMediaClick={onUploadClick}
    />
  ) : (
    <Type2Cover
      variant="banner"
      title={title}
      gradient={gradient}
      cardImage={cardImage}
      titleColor={titleColor}
      onMediaClick={onUploadClick}
      placeholder={
        <Dropzone
          variant="onMedia"
          icon={ImageIcon}
          title=""
          aria-label={m.cover}
          onClick={onUploadClick}
          disabled={!onUploadClick}
          className="h-full !p-3"
        />
      }
    />
  )
}

/** Карточка «как в вебе» для текущего языка правки. */
export const CoverSquare = ({
  title,
  coverType,
  gradient,
  cardImage,
  titleColor,
  onUploadClick,
  progress = null,
  authorName,
  authorAvatar,
  viewsCount,
}: SquareProps) => {
  const m = useMessages(messages)
  const placeholder = squarePlaceholder(m, onUploadClick)

  const card = (
    <WebInstructionCard
      coverType={coverType}
      title={title}
      gradient={gradient}
      cardImage={cardImage}
      titleColor={titleColor}
      authorName={authorName}
      authorAvatar={authorAvatar}
      viewsCount={viewsCount}
      placeholder={placeholder}
    />
  )
  // Загрузка — тот же вид, что у всех картинок студии, поверх всей карточки: и
  // первой обложки, и замены уже стоящей (раньше при замене не было видно ничего).
  const withProgress = (node: ReactNode) => (
    <div className="relative rounded-xl">
      {node}
      <UploadProgressOverlay progress={progress} />
    </div>
  )
  // Картинка уже есть — клик по карточке меняет её (зона загрузки видна только без картинки).
  return withProgress(
    cardImage && onUploadClick ? (
      <button
        type="button"
        onClick={onUploadClick}
        aria-label={m.replaceCover}
        className="block w-full rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {card}
      </button>
    ) : (
      card
    ),
  )
}
